import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Quote } from "lucide-react";
import { FadeUp, SectionHeading } from "@/components/Reveal";
import { TESTIMONIALS } from "@/data/content";

const INTERVAL = 6000;

export default function Testimonials() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setI((n) => (n + 1) % TESTIMONIALS.length), INTERVAL);
    return () => clearInterval(t);
  }, [paused]);

  const t = TESTIMONIALS[i];

  return (
    <section className="relative overflow-hidden border-t border-white/10" data-testid="testimonials-section">
      <div className="saffron-glow pointer-events-none absolute inset-0" />
      <div className="relative mx-auto grid max-w-[1440px] grid-cols-1 gap-14 px-6 py-24 lg:grid-cols-12 lg:px-12 lg:py-32">
        <div className="lg:col-span-4">
          <SectionHeading overline="Testimonials — क्या कहते हैं" lines={["Words from", "the ground."]} testId="testimonials-heading" />
          <FadeUp delay={0.15}>
            <div className="mt-10 flex gap-2" data-testid="testimonial-dots">
              {TESTIMONIALS.map((_, k) => (
                <button
                  key={k}
                  type="button"
                  aria-label={`Testimonial ${k + 1}`}
                  data-testid={`testimonial-dot-${k}`}
                  onClick={() => setI(k)}
                  className="relative h-1.5 w-12 overflow-hidden rounded-full bg-white/10"
                >
                  {k === i && (
                    <motion.span
                      key={`${k}-${paused}`}
                      className="absolute inset-y-0 left-0 bg-saffron"
                      initial={{ width: "0%" }}
                      animate={{ width: paused ? "100%" : "100%" }}
                      transition={{ duration: paused ? 0 : INTERVAL / 1000, ease: "linear" }}
                    />
                  )}
                </button>
              ))}
            </div>
          </FadeUp>
        </div>

        <div className="lg:col-span-8" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
          <FadeUp delay={0.1}>
            <div className="relative min-h-[300px] rounded-2xl border border-white/10 bg-coal/60 p-8 lg:p-12" data-testid="testimonial-card">
              <Quote className="absolute right-8 top-8 h-10 w-10 text-brand/30" />
              <AnimatePresence mode="wait">
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 24, filter: "blur(6px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  exit={{ opacity: 0, y: -24, filter: "blur(6px)" }}
                  transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                >
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-saffron">{t.tag}</p>
                  <p className={`mt-6 max-w-3xl text-xl leading-relaxed text-paper sm:text-2xl lg:text-3xl ${/[\u0900-\u097F]/.test(t.quote) ? "font-hindi" : "font-script italic"}`} data-testid="testimonial-quote">
                    “{t.quote}”
                  </p>
                  <div className="mt-10 flex items-center gap-4">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-brand font-display text-base font-bold text-white">
                      {t.name[0]}
                    </span>
                    <div>
                      <p className="font-display text-base font-bold uppercase tracking-tight text-paper">{t.name}</p>
                      <p className="text-xs text-zinc-500">{t.role}</p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
